import * as React from "react";

/** Set on `document.body` by `react-remove-scroll` while a lock is active. */
const SCROLL_LOCK_ATTRIBUTE = "data-scroll-locked";

/**
 * Keeps a floating layer scrollable while the page behind it is scroll-locked.
 *
 * Radix modals (Dialog/Sheet) lock the page with `react-remove-scroll`, whose
 * `wheel`/`touchmove` listener on `document` cancels scrolling for any target
 * that is neither the lock nor its content shard. Dropdowns, popovers, and
 * select menus portal to `document.body`, so one opened from inside a modal
 * lands outside the lock and its list cannot be scrolled at all.
 *
 * That listener runs in the bubble phase, so stopping propagation at the layer
 * keeps the native scroll while still leaving the page behind the modal
 * locked. The guard is inert unless a lock is active, so scroll chaining
 * outside modals behaves exactly as it did before.
 *
 * Returns a ref to attach to the layer's outermost element; compose it with a
 * forwarded ref via `useComposedRefs`.
 */
export function useScrollLockPassthrough<T extends HTMLElement>(): React.RefCallback<T> {
  return React.useCallback((node: T | null) => {
    if (!node) return;
    const stopWhileLocked = (event: Event) => {
      if (document.body.hasAttribute(SCROLL_LOCK_ATTRIBUTE)) {
        event.stopPropagation();
      }
    };
    node.addEventListener("wheel", stopWhileLocked, { passive: true });
    node.addEventListener("touchmove", stopWhileLocked, { passive: true });
    return () => {
      node.removeEventListener("wheel", stopWhileLocked);
      node.removeEventListener("touchmove", stopWhileLocked);
    };
  }, []);
}
